import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Upload, Lock, AlertTriangle } from "lucide-react";

/** Edit an existing listing: name, price, description, category, tags —
 *  plus a CAD-file swap. The file is locked once the design has sold. */
export default function EditListingModal({ listing, cats = [], onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", description: "", category_id: "", price: "", tags: "",
    metal_weight: "", stone_details: "",
  });
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!listing) return;
    setF({
      title: listing.title || "",
      description: listing.description || "",
      category_id: listing.category_id || "",
      price: String(listing.price ?? ""),
      tags: (listing.tags || []).join(", "),
      metal_weight: listing.metal_weight ?? listing.cam_weight ?? "",
      stone_details: listing.stone_details || "",
    });
  }, [listing]);

  if (!listing) return null;
  const fileLocked = !!(listing.file_locked || listing.has_sales || listing.sales_count > 0);

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Design name can't be empty");
    if (!f.category_id) return toast.error("Please pick a category");
    const price = parseFloat(f.price);
    if (!(price > 0)) return toast.error("Enter a valid price");
    setSaving(true);
    try {
      await api.put(`/listings/${listing.id}`, {
        title: f.title.trim(),
        description: f.description,
        category_id: f.category_id,
        price,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        metal_weight: f.metal_weight === "" ? null : parseFloat(f.metal_weight),
        stone_details: f.stone_details || null,
      });
      toast.success("Listing updated");
      onSaved?.();
      onClose();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally { setSaving(false); }
  };

  const replaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(stl|3dm)$/i.test(file.name)) return toast.error("Please pick a .stl or .3dm file");
    if (!window.confirm(
      "Replace the CAD file on this design?\n\n" +
      "• Current studio photos are deleted and new renders are generated\n" +
      "• The listing stays live while our team verifies the new file"
    )) return;
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/seller/listings/${listing.id}/replace-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File replaced — new studio renders are being generated.");
      onSaved?.({ replaced: true, listing });
      onClose();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally { setReplacing(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/70 flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-6" data-testid="edit-listing-modal">
      <form
        onSubmit={save}
        className="bg-card border border-border rounded-lg w-full max-w-2xl shadow-2xl"
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Edit design</h3>
            <p className="text-xs text-muted-foreground truncate max-w-sm">{listing.product_id ? `${listing.product_id} · ` : ""}{listing.title}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="edit-listing-close" className="rounded p-1 hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <Row label="Design name">
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="edit-title" className="inp" />
          </Row>
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Price (₹)">
              <input type="number" min="1" step="1" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="edit-price" className="inp" />
            </Row>
            <Row label="Category">
              <select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="edit-category" className="inp">
                <option value="">Select…</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Row>
          </div>
          <Row label="Description">
            <textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="edit-description" className="inp resize-y" />
          </Row>
          <div className="grid sm:grid-cols-2 gap-4">
            <Row label="Tags (comma separated)">
              <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="edit-tags" className="inp" />
            </Row>
            <Row label="CAM weight (g)">
              <input type="number" step="0.01" value={f.metal_weight} onChange={(e) => setF({ ...f, metal_weight: e.target.value })} data-testid="edit-weight" className="inp" />
            </Row>
          </div>
          <Row label="Stone details">
            <input value={f.stone_details} onChange={(e) => setF({ ...f, stone_details: e.target.value })} data-testid="edit-stones" className="inp" />
          </Row>

          {/* ---- CAD file ---- */}
          <div className="border-t border-border pt-4">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-2">CAD file</p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground font-mono">
                Current: <b className="uppercase">{listing.file_type}</b>
                {listing.file_size ? ` · ${(listing.file_size / 1048576).toFixed(1)} MB` : ""}
              </p>
              {fileLocked ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-md px-3 py-1.5" data-testid="edit-file-locked">
                  <Lock className="h-3.5 w-3.5" /> Locked — this design has sales
                </span>
              ) : (
                <>
                  <input ref={fileRef} type="file" accept=".stl,.3dm" onChange={replaceFile} className="hidden" data-testid="edit-file-input" />
                  <button
                    type="button"
                    disabled={replacing}
                    onClick={() => fileRef.current?.click()}
                    data-testid="edit-replace-file"
                    className="inline-flex items-center gap-1.5 border border-amber-500/50 text-amber-600 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-amber-500/5 disabled:opacity-60"
                  >
                    {replacing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    Replace CAD file
                  </button>
                </>
              )}
            </div>
            {!fileLocked && (
              <p className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-px" />
                Replacing the file deletes the current studio photos and re-runs the renders. Your listing stays live while our team verifies the new file.
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded-md hover:bg-secondary">Cancel</button>
          <button
            disabled={saving}
            data-testid="edit-listing-save"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

const Row = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium">{label}</span>
    <div className="mt-1 [&_.inp]:w-full [&_.inp]:border [&_.inp]:border-border [&_.inp]:rounded-md [&_.inp]:p-2.5 [&_.inp]:text-base [&_.inp]:bg-background [&_.inp]:focus:outline-none [&_.inp]:focus:border-primary">
      {children}
    </div>
  </label>
);
